/**
 * Rental Detail hook using React Relay
 *
 * Fetches a single rental with all its details including billing,
 * rate, perk, coupon, and support messages.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useRentalDetailRelayQuery as UseRentalDetailRelayQuery } from './__generated__/useRentalDetailRelayQuery.graphql';
import type { useRentalDetailRelayByOldIdQuery as UseRentalDetailRelayByOldIdQuery } from './__generated__/useRentalDetailRelayByOldIdQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for single rental detail by ID
 */
const RentalDetailQuery = graphql`
  query useRentalDetailRelayQuery($id: ID!) {
    getRental(id: $id) {
      id
      oldId
      organizationId
      createdOn

      # User
      user {
        id
        phone
        fullName
        firstName
        lastName
      }
      userId
      userOldId

      # Hardware & System
      hardware {
        id
        oldId
        number
        label
        hardwareType {
          id
          name
        }
        hardwareProduct {
          id
          name
          hasLocationTracking
        }
        children {
          id
          oldId
          number
          label
          hardwareType {
            id
            name
          }
        }
      }
      hardwareOldId
      system {
        id
        oldId
        name
        countryCode
        billingLive
        roundTrip
        newHardware
      }
      systemId
      systemOldId

      # Locations
      startLocation {
        id
        oldId
        name
        lat
        lng
      }
      endLocation {
        id
        oldId
        name
        lat
        lng
      }
      startLat
      startLng
      endLat
      endLng

      # Timing
      startTime
      endTime
      duration
      endBy
      gracePeriod
      effectiveGracePeriod
      endedWithinGracePeriod

      # Distance
      distance
      distanceFormatted
      tripDistance

      # Billing
      chargeAmount
      chargeTotal
      chargeId
      chargeSuccess
      charge {
        id
        amount
        status
        description
        created
      }
      ccHold {
        id
        amount
        status
        description
        created
      }
      taxAmount
      taxRate
      usageSubtotal
      adminModification
      adminModificationReason
      parkingCredit
      fees {
        id
        name
        amount
        appliedPostTax
      }
      feeIds
      feesPostTaxAmount
      refundAmount
      refundComment
      walletTransaction {
        id
        oldId
        amount
        description
        type
        createdOn
      }

      # Pending Charge
      chargePending
      chargePendingDecisionDate
      chargePendingNote
      chargePendingStatus

      # Void
      isVoidCharge
      voidReason
      voidedOn
      voidBy
      voidByAdmin {
        id
        email
        firstName
        lastName
      }

      # Force End
      forceEndType
      forceEndTime
      forceEndAck

      # Triggered By
      startTriggeredBy
      endTriggeredBy
      startType
      endType

      # Images
      images
      endImageUrl

      # Pricing
      coupon {
        id
        oldId
        code
        name
        discountType
        discount
        duration
        uses
        active
        created
      }
      rate {
        id
        oldId
        name
        baseRate
        baseRateIncludedTime
        pricePerPeriod
        periodDuration
        freeTime
        creditCardHold
        minimumWalletDeposit
        hidden
        membershipRequired
        maxPeriods
        maxRentals
        activeOn
        archivedOn
      }
      perk {
        id
        oldId
        membershipOldId
        freeTime
        minutes
        moneyOff
        durationMoneyOff
        discount
        waiveBaseRate
        unlocks
        membership {
          name
        }
      }

      # Support Messages
      supportMessages {
        id
        oldId
        content
        created
        owner
        notes
        state
        type
        subtype
        tags
      }

      # Status
      completed
      rating
      failedStart
      canceledOn
      reviewStatus
    }
  }
`;

/**
 * Query for single rental detail by old ID (for backwards compatibility)
 */
const RentalDetailByOldIdQuery = graphql`
  query useRentalDetailRelayByOldIdQuery($filter: RentalFilterInput, $first: Int) {
    listRentals(filter: $filter, first: $first) {
      edges {
        node {
          id
          oldId
          organizationId
          createdOn

          # User
          user {
            id
            phone
            fullName
            firstName
            lastName
          }
          userId
          userOldId

          # Hardware & System
          hardware {
            id
            oldId
            number
            label
            hardwareType {
              id
              name
            }
            hardwareProduct {
              id
              name
              hasLocationTracking
            }
            children {
              id
              oldId
              number
              label
              hardwareType {
                id
                name
              }
            }
          }
          hardwareOldId
          system {
            id
            oldId
            name
            countryCode
            billingLive
            roundTrip
            newHardware
          }
          systemId
          systemOldId

          # Locations
          startLocation {
            id
            oldId
            name
            lat
            lng
          }
          endLocation {
            id
            oldId
            name
            lat
            lng
          }
          startLat
          startLng
          endLat
          endLng

          # Timing
          startTime
          endTime
          duration
          endBy
          gracePeriod
          effectiveGracePeriod
          endedWithinGracePeriod

          # Distance
          distance
          distanceFormatted
          tripDistance

          # Billing
          chargeAmount
          chargeTotal
          chargeId
          chargeSuccess
          charge {
            id
            amount
            status
            description
            created
          }
          ccHold {
            id
            amount
            status
            description
            created
          }
          taxAmount
          taxRate
          usageSubtotal
          adminModification
          adminModificationReason
          parkingCredit
          fees {
            id
            name
            amount
            appliedPostTax
          }
          feeIds
          feesPostTaxAmount
          refundAmount
          refundComment
          walletTransaction {
            id
            oldId
            amount
            description
            type
            createdOn
          }

          # Pending Charge
          chargePending
          chargePendingDecisionDate
          chargePendingNote
          chargePendingStatus

          # Void
          isVoidCharge
          voidReason
          voidedOn
          voidBy
          voidByAdmin {
            id
            email
            firstName
            lastName
          }

          # Force End
          forceEndType
          forceEndTime
          forceEndAck

          # Triggered By
          startTriggeredBy
          endTriggeredBy
          startType
          endType

          # Images
          images
          endImageUrl

          # Pricing
          coupon {
            id
            oldId
            code
            name
            discountType
            discount
            duration
            uses
            active
            created
          }
          rate {
            id
            oldId
            name
            baseRate
            baseRateIncludedTime
            pricePerPeriod
            periodDuration
            freeTime
            creditCardHold
            minimumWalletDeposit
            hidden
            membershipRequired
            maxPeriods
            maxRentals
            activeOn
            archivedOn
          }
          perk {
            id
            oldId
            membershipOldId
            freeTime
            minutes
            moneyOff
            durationMoneyOff
            discount
            waiveBaseRate
            unlocks
            membership {
              name
            }
          }

          # Support Messages
          supportMessages {
            id
            oldId
            content
            created
            owner
            notes
            state
            type
            subtype
            tags
          }

          # Status
          completed
          rating
          failedStart
          canceledOn
          reviewStatus
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * User type from GraphQL rental detail
 */
export interface RentalDetailUserGraphQL {
  id: string;
  phone: string;
  fullName?: string | null;
  firstName: string;
  lastName: string;
}

/**
 * Hardware type from GraphQL rental detail
 */
export interface RentalDetailHardwareGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
  label?: string | null;
  hardwareType?: {
    id: string;
    name?: string | null;
  } | null;
  hardwareProduct?: {
    id: string;
    name?: string | null;
    hasLocationTracking?: boolean | null;
  } | null;
  children?: RentalDetailHardwareGraphQL[] | null;
}

/**
 * Location type from GraphQL rental detail
 */
export interface RentalDetailLocationGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  lat?: number | null;
  lng?: number | null;
}

/**
 * System type from GraphQL rental detail
 */
export interface RentalDetailSystemGraphQL {
  id: string;
  oldId?: string | null;
  name?: string | null;
  countryCode?: string | null;
  billingLive?: boolean | null;
  roundTrip?: boolean | null;
  newHardware?: boolean | null;
}

/**
 * Charge type from GraphQL
 */
export interface ChargeGraphQL {
  id: string;
  amount?: number | null;
  status?: string | null;
  description?: string | null;
  created?: string | null;
}

/**
 * Fee type from GraphQL
 */
export interface FeeGraphQL {
  id: string;
  name: string;
  amount: number;
  appliedPostTax: boolean;
}

/**
 * Wallet Transaction type from GraphQL
 */
export interface WalletTransactionGraphQL {
  id: string;
  oldId?: string | null;
  amount?: number | null;
  description?: string | null;
  type?: string | null;
  createdOn?: string | null;
}

/**
 * Admin user type from GraphQL
 */
export interface AdminUserGraphQL {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Coupon type from GraphQL
 */
export interface CouponGraphQL {
  id: string;
  oldId?: string | null;
  code?: string | null;
  name: string;
  discountType?: string | null;
  discount?: number | null;
  duration?: number | null;
  uses?: number | null;
  active?: boolean | null;
  created?: string | null;
}

/**
 * Rate type from GraphQL
 */
export interface RateGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  baseRate?: number | null;
  baseRateIncludedTime?: number | null;
  pricePerPeriod?: number | null;
  periodDuration?: number | null;
  freeTime?: number | null;
  creditCardHold?: number | null;
  minimumWalletDeposit?: number | null;
  hidden?: boolean | null;
  membershipRequired: boolean;
  maxPeriods?: number | null;
  maxRentals?: number | null;
  activeOn?: string | null;
  archivedOn?: string | null;
}

/**
 * Perk type from GraphQL
 */
export interface PerkGraphQL {
  id: string;
  oldId?: string | null;
  membershipOldId?: string | null;
  freeTime?: number | null;
  minutes?: number | null;
  moneyOff?: number | null;
  durationMoneyOff?: number | null;
  discount?: number | null;
  waiveBaseRate?: boolean | null;
  unlocks?: number | null;
  membership?: {
    name?: string | null;
  } | null;
}

/**
 * Support message / Error report type from GraphQL
 */
export interface SupportMessageGraphQL {
  id: string;
  oldId?: string | null;
  content?: string | null;
  created?: string | null;
  owner?: string | null;
  notes?: string | null;
  state?: number | null;
  type?: string | null;
  subtype?: string | null;
  tags?: readonly string[] | null;
}

/**
 * Full rental detail type from GraphQL
 */
export interface RentalDetailGraphQL {
  id: string;
  oldId?: string | null;
  organizationId?: string | null;
  createdOn?: string | null;

  // User
  user?: RentalDetailUserGraphQL | null;
  userId?: string | null;
  userOldId?: string | null;

  // Hardware & System
  hardware?: RentalDetailHardwareGraphQL | null;
  hardwareOldId?: string | null;
  system?: RentalDetailSystemGraphQL | null;
  systemId?: string | null;
  systemOldId?: string | null;

  // Locations
  startLocation?: RentalDetailLocationGraphQL | null;
  endLocation?: RentalDetailLocationGraphQL | null;
  startLat?: number | null;
  startLng?: number | null;
  endLat?: number | null;
  endLng?: number | null;

  // Timing
  startTime?: string | null;
  endTime?: string | null;
  duration?: number | null;
  endBy?: string | null;
  gracePeriod?: number | null;
  effectiveGracePeriod?: number | null;
  endedWithinGracePeriod?: boolean | null;

  // Distance
  distance?: number | null;
  distanceFormatted?: string | null;
  tripDistance?: number | null;

  // Billing
  chargeAmount?: number | null;
  chargeTotal?: number | null;
  chargeId?: string | null;
  chargeSuccess?: boolean | null;
  charge?: ChargeGraphQL | null;
  ccHold?: ChargeGraphQL | null;
  taxAmount?: string | null;
  taxRate?: string | null;
  usageSubtotal?: number | null;
  adminModification?: number | null;
  adminModificationReason?: string | null;
  parkingCredit?: number | null;
  fees?: readonly FeeGraphQL[] | null;
  feeIds?: readonly string[] | null;
  feesPostTaxAmount?: number | null;
  refundAmount?: number | null;
  refundComment?: string | null;
  walletTransaction?: WalletTransactionGraphQL | null;

  // Pending Charge
  chargePending?: boolean | null;
  chargePendingDecisionDate?: string | null;
  chargePendingNote?: string | null;
  chargePendingStatus?: boolean | null;

  // Void
  isVoidCharge: boolean;
  voidReason?: string | null;
  voidedOn?: string | null;
  voidBy?: string | null;
  voidByAdmin?: AdminUserGraphQL | null;

  // Force End
  forceEndType?: string | null;
  forceEndTime?: string | null;
  forceEndAck?: boolean | null;

  // Triggered By
  startTriggeredBy?: string | null;
  endTriggeredBy?: string | null;
  startType?: string | null;
  endType?: string | null;

  // Images
  images?: readonly string[] | null;
  endImageUrl?: string | null;

  // Pricing
  coupon?: CouponGraphQL | null;
  rate?: RateGraphQL | null;
  perk?: PerkGraphQL | null;

  // Support Messages
  supportMessages?: readonly SupportMessageGraphQL[];

  // Status
  completed?: boolean | null;
  rating?: number | null;
  failedStart?: boolean | null;
  canceledOn?: string | null;
  // null = legacy rental (no review), otherwise 'pending' | 'approved' | 'rejected'
  reviewStatus?: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching rental detail using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function RentalDetailPage({ rentalId }: { rentalId: string }) {
 *   return (
 *     <Suspense fallback={<Loading />}>
 *       <RentalDetailContent rentalId={rentalId} />
 *     </Suspense>
 *   );
 * }
 *
 * function RentalDetailContent({ rentalId }: { rentalId: string }) {
 *   const rental = useRentalDetailRelay(rentalId);
 *   return <RentalDetailView rental={rental} />;
 * }
 */
export function useRentalDetailRelay(rentalId: string) {
  const data = useLazyLoadQuery<UseRentalDetailRelayQuery>(RentalDetailQuery, {
    id: rentalId,
  });

  return data.getRental as RentalDetailGraphQL | null;
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for rental detail
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 *
 * @param rentalId - Can be either the new UUID ID or the legacy oldId
 * @param options.enabled - Whether to enable the query
 * @param options.useOldId - If true, uses oldId filter instead of direct ID lookup
 *
 * @example
 * const { data, isLoading, refetch } = useRentalDetailRelayQuery(rentalId);
 *
 * if (isLoading) return <Loading />;
 * return <RentalDetailView rental={data} />;
 */
export function useRentalDetailRelayQuery(
  rentalId: string | undefined,
  options?: {
    enabled?: boolean;
    useOldId?: boolean;
  }
) {
  const [data, setData] = useState<RentalDetailGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(rentalId);
  const useOldId = options?.useOldId ?? true; // Default to oldId for backwards compatibility

  useEffect(() => {
    if (!enabled || !rentalId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        let rental: RentalDetailGraphQL | null = null;

        if (useOldId) {
          // Use filter by oldId
          const result = await fetchQuery<UseRentalDetailRelayByOldIdQuery>(
            relayEnvironment,
            RentalDetailByOldIdQuery,
            {
              filter: { oldId: { eq: rentalId } },
              first: 1,
            }
          ).toPromise();

          if (result?.listRentals?.edges?.[0]?.node) {
            rental = result.listRentals.edges[0].node as RentalDetailGraphQL;
          }
        } else {
          // Use direct ID lookup
          const result = await fetchQuery<UseRentalDetailRelayQuery>(
            relayEnvironment,
            RentalDetailQuery,
            { id: rentalId }
          ).toPromise();

          if (result) {
            rental = result.getRental as RentalDetailGraphQL | null;
          }
        }

        if (cancelled) return;

        setData(rental);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch rental'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, rentalId, useOldId, isFetched, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      isRefetching,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, isRefetching, error, refetch]
  );
}
